import React, { useRef, useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Upload, Loader2, ShieldCheck, IdCard, User as UserIcon, Camera } from 'lucide-react';
import { toast } from 'sonner';
import { applyOrganiser, uploadFile } from '../apis/ride-24';

const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf'];
const MAX_BYTES = 5 * 1024 * 1024;

const ID_TYPE_OPTIONS = [
  { value: 'aadhar', label: 'Aadhar' },
  { value: 'driving_license', label: 'Driving License' },
  { value: 'passport', label: 'Passport' },
];

const apiError = (error, fallback) =>
  error.response?.data?.detail || error.response?.data?.message || fallback;

/**
 * Reusable "Be a verified Organiser" application form.
 */
const OrganiserApplyForm = ({ defaultName = '', onSubmitted, onCancel, compact = false }) => {
  const photoInputRef = useRef(null);
  const docInputRef = useRef(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [docUploading, setDocUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    full_name: defaultName,
    phone: '',
    id_type: '',
    id_number: '',
    photo_path: '',
    id_document_path: '',
  });

  const validateFile = (file) => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Only JPG, PNG, WebP, or PDF files are allowed');
      return false;
    }
    if (file.size > MAX_BYTES) {
      toast.error('File must be 5 MB or smaller');
      return false;
    }
    return true;
  };

  const uploadPrivate = async (file) => {
    const uploaded = await uploadFile(file, { isPrivate: true });
    return uploaded.path;
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !validateFile(file)) return;
    setPhotoUploading(true);
    try {
      const path = await uploadPrivate(file);
      setForm((f) => ({ ...f, photo_path: path }));
      toast.success('Photo uploaded');
    } catch (err) {
      toast.error(apiError(err, 'Photo upload failed'));
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleDocChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !validateFile(file)) return;
    setDocUploading(true);
    try {
      const path = await uploadPrivate(file);
      setForm((f) => ({ ...f, id_document_path: path }));
      toast.success('ID document uploaded');
    } catch (err) {
      toast.error(apiError(err, 'Document upload failed'));
    } finally {
      setDocUploading(false);
    }
  };

  const isValid =
    form.full_name.trim() &&
    form.id_type &&
    form.id_number.trim() &&
    form.photo_path &&
    form.id_document_path;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid) return;
    setSubmitting(true);
    try {
      await applyOrganiser({
        full_name: form.full_name.trim(),
        phone: form.phone.trim() || null,
        id_type: form.id_type,
        id_number: form.id_number.trim(),
        photo_path: form.photo_path,
        id_document_path: form.id_document_path,
      });
      toast.success('Application submitted for admin review');
      onSubmitted && onSubmitted();
    } catch (err) {
      toast.error(apiError(err, 'Failed to submit application'));
    } finally {
      setSubmitting(false);
    }
  };

  const inputDark =
    'bg-gray-950 border-gray-700 text-gray-100 placeholder:text-gray-500 focus-visible:ring-red-600 focus-visible:ring-offset-0';

  return (
    <form
      onSubmit={handleSubmit}
      className={`space-y-5 ${compact ? '' : 'p-1'}`}
      data-testid="organiser-apply-form"
    >
      <div className="flex items-start gap-3 rounded-sm bg-red-950/30 border border-red-900/40 p-3">
        <ShieldCheck className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
        <p className="text-sm text-red-200">
          <span className="font-heading uppercase tracking-wider text-red-400 block mb-1">
            Be a verified Organiser
          </span>
          Only verified riders can create rides. Submit your details — an admin will review and approve your application.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <Label htmlFor="oa_full_name" className="text-gray-200">Full name (as on ID) *</Label>
          <Input
            id="oa_full_name"
            value={form.full_name}
            onChange={(e) => setForm({ ...form, full_name: e.target.value })}
            className={inputDark}
            required
            data-testid="organiser-full-name-input"
          />
        </div>
        <div>
          <Label htmlFor="oa_phone" className="text-gray-200">Phone (optional)</Label>
          <Input
            id="oa_phone"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            placeholder="+91 ..."
            className={inputDark}
            data-testid="organiser-phone-input"
          />
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <Label className="text-gray-200">ID type *</Label>
          <Select value={form.id_type} onValueChange={(v) => setForm({ ...form, id_type: v })}>
            <SelectTrigger className={inputDark} data-testid="organiser-id-type-select">
              <SelectValue placeholder="Select ID" />
            </SelectTrigger>
            <SelectContent className="z-[100] bg-gray-900 border border-gray-800 text-gray-200">
              {ID_TYPE_OPTIONS.map((opt) => (
                <SelectItem
                  key={opt.value}
                  value={opt.value}
                  data-testid={`organiser-id-type-${opt.value}`}
                  className="focus:bg-red-600 focus:text-white"
                >
                  <div className="flex items-center">
                    <IdCard className="w-4 h-4 mr-2" />
                    {opt.label}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="oa_id_number" className="text-gray-200">ID number *</Label>
          <Input
            id="oa_id_number"
            value={form.id_number}
            onChange={(e) => setForm({ ...form, id_number: e.target.value })}
            placeholder="e.g., 1234-5678-9012"
            className={inputDark}
            required
            data-testid="organiser-id-number-input"
          />
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <FileTile
          label="Your photo *"
          hint="Clear face photo for verification"
          uploaded={!!form.photo_path}
          uploading={photoUploading}
          onClick={() => photoInputRef.current?.click()}
          testId="organiser-photo-tile"
          icon={<UserIcon className="w-6 h-6 mb-1" />}
        />
        <FileTile
          label="ID document *"
          hint="Aadhar / DL / Passport — image or PDF"
          uploaded={!!form.id_document_path}
          uploading={docUploading}
          onClick={() => docInputRef.current?.click()}
          testId="organiser-id-doc-tile"
          icon={<Camera className="w-6 h-6 mb-1" />}
        />
        <input
          ref={photoInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={handlePhotoChange}
          className="hidden"
          data-testid="organiser-photo-input"
        />
        <input
          ref={docInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,application/pdf"
          onChange={handleDocChange}
          className="hidden"
          data-testid="organiser-id-doc-input"
        />
      </div>

      <div className="flex justify-end gap-3 pt-2">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            className="border-gray-700 bg-transparent text-gray-200 hover:bg-gray-800 hover:text-white"
            data-testid="organiser-cancel-button"
          >
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          disabled={!isValid || submitting}
          className="bg-red-600 hover:bg-red-700 text-white border-0 font-heading uppercase tracking-widest disabled:opacity-50"
          data-testid="organiser-submit-button"
        >
          {submitting ? 'Submitting…' : 'Submit for verification'}
        </Button>
      </div>
    </form>
  );
};

const FileTile = ({ label, hint, uploaded, uploading, onClick, testId, icon }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={uploading}
    className={`aspect-[3/2] rounded-md border-2 border-dashed p-4 text-left flex flex-col justify-center items-center transition-colors ${
      uploaded
        ? 'border-red-600 bg-red-950/30 text-red-300'
        : 'border-gray-700 hover:border-red-600 bg-gray-950 text-gray-400 hover:text-red-400'
    } disabled:opacity-50`}
    data-testid={testId}
  >
    {uploading ? (
      <Loader2 className="w-6 h-6 animate-spin" />
    ) : uploaded ? (
      <>
        <span className="text-2xl mb-1" aria-hidden="true">✓</span>
        <div className="text-sm font-medium">{label} uploaded</div>
        <div className="text-xs opacity-70">Tap to replace</div>
      </>
    ) : (
      <>
        {icon}
        <div className="text-sm font-medium">{label}</div>
        <div className="text-xs opacity-70 text-center">
          <Upload className="inline w-3 h-3 mr-1" />
          {hint}
        </div>
      </>
    )}
  </button>
);

export default OrganiserApplyForm;
